'use client';

import { useAnalytics } from '@repo/platform-analytics/hooks/useAnalytics';
import { useAnonymousId } from '@repo/platform-analytics/SegmentAnalyticsProvider';
import { getDeviceIdWithoutHmac } from '@repo/utils/analytics/getDeviceId';
import { useDeviceType } from '@repo/utils/device-type/hooks';
import { createContext, type PropsWithChildren, useContext, useEffect, useRef, useState } from 'react';
import type { ABSmartlyContext } from '../types/absmartly';
import { getABSmartlyEventLogger } from '../utils/getABSmartlyEventLogger';
import { type HydrateContextParams, hydrateClientContext } from '../utils/hydrateClientContext';
import type { ABTestVariantResult } from '../utils/variants';
import { abSmartlySDKConfig } from './config';
import { LogStaticExperiments } from './LogStaticExperiments';
type ABSmartlyProviderProps = PropsWithChildren<Omit<HydrateContextParams, 'anonymousId' | 'deviceId' | 'deviceType' | 'sdkConfig'> & {
  staticExperimentResult: ABTestVariantResult;
}>;
export const ABSmartly = createContext<ABSmartlyContext | null>(null);
const getDeviceId = (ref: React.RefObject<string>) => {
  if (!ref.current && typeof window !== 'undefined') {
    ref.current = getDeviceIdWithoutHmac();
  }
  return ref.current;
};
export const useABSmartlyContext = () => {
  return useContext(ABSmartly);
};
export const ABSmartlyProvider = ({
  children,
  contextData,
  staticExperimentResult
}: ABSmartlyProviderProps) => {
  const [context, setContext] = useState<ABSmartlyContext | null>(null);
  const {
    analytics,
    deployableVersion,
    gitSha
  } = useAnalytics();
  const anonymousId = useAnonymousId();
  const deviceIdRef = useRef('');
  const deviceId = getDeviceId(deviceIdRef);
  const deviceType = useDeviceType();
  useEffect(() => {
    if (context || !analytics) {
      return;
    }
    const clientContext = hydrateClientContext({
      sdkConfig: {
        ...abSmartlySDKConfig,
        eventLogger: getABSmartlyEventLogger({
          analytics,
          anonymousId,
          deployableVersion,
          deviceId,
          gitSha,
          staticExperimentResult
        })
      },
      anonymousId,
      deviceId,
      deviceType,
      contextData
    });
    setContext(clientContext);
  }, [analytics, anonymousId, deviceId, deviceType, contextData]);
  return <ABSmartly.Provider value={context}>
      <LogStaticExperiments serverSideExperimentDecision={staticExperimentResult}>
        {children}
      </LogStaticExperiments>
    </ABSmartly.Provider>;
};